import { EditOutlined } from "@mui/icons-material";
import { Box, IconButton, Tooltip as MuiTooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import React, { type FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ThemedSvg } from "../../components/UIToolkit/ThemedSvg";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useShiftHighlight } from "../../contexts/ShiftHighlight";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useWeekViewContext } from "../../contexts/WeekView";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { BADGE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { censusReplacementSummaryLines } from "../../utils/censusReplacement";
import { byDimensionsOf } from "../../utils/censusResolution";
import { ChangesSource, type DayStats, type EmploymentType, type Nurse, type NurseFullRole, type NurseInShift } from "../../utils/models";
import { getCensus, getCensusSourceLabel, getCensusSourceName, getChipColor, getShiftCensus, getShiftIndexByShiftTimes } from "../../utils/utilFunctions";
import { NurseContainer } from "../nurseContainer";
import NurseHoursChip from "../staffingCheaps/HoursChip";
import { CensusBreakdownTooltipContent } from "../UIToolkit/Popups/CensusBreakdownTooltipContent";
import { CensusDisplay } from "./CensusDisplay";
import Style from "./StaffingBlock.module.scss";

type DayBlockProps = {
	shiftNo: number;
	dayNo: number;
	rolesToDisplay: NurseFullRole[];
	employmentTypesToDisplay: EmploymentType[];
	selectedJobIds?: string[];
	isFullDay: boolean;
	onAddClick: (dayIndex: number) => void;
	highlightedNurseId?: number;
	/** Marks this day's Edit Staff pencil as the Annie walkthrough anchor. Set on one day per page. */
	isPostShiftAnchor?: boolean;
};

export const DayBlock: FC<DayBlockProps> = memo(
	({ shiftNo, dayNo, rolesToDisplay, employmentTypesToDisplay, selectedJobIds, isFullDay, onAddClick, highlightedNurseId, isPostShiftAnchor }) => {
		const { weekIndex } = useWeekIndex();
		const { settings } = useUserSettingsContext();
		const { isShiftIdHighlighted, clearShiftHighlight, highlightShift } = useShiftHighlight(shiftNo, dayNo);
		const { hospitalData, isSnf, chosenDepartment, isHospital, isHospitalView, isHospitalDemo } = useHospitalData();
		const formatPersonName = useFormatPersonName();
		const censusSourceLabel = getCensusSourceLabel(chosenDepartment, isHospitalView || isHospitalDemo);
		const { t } = useTranslation();
		const navigate = useNavigate();
		const { weekViewData } = useWeekViewContext();
		const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);

		const isActualLoaded = useMemo<boolean>(
			() => weekViewData[dayNo].date < DateTime.now().startOf("day") && !!weekViewData[dayNo].shiftsActual.length,
			[weekViewData, dayNo],
		);

		const dayStats: DayStats | null = useMemo(() => {
			if (isHospital) return null;

			return isActualLoaded ? weekViewData[dayNo].dayStatsActual : weekViewData[dayNo].dayStats;
		}, [weekViewData, dayNo, isActualLoaded, isHospital]);

		const chipColor = useMemo(() => {
			if (!dayStats) return { backgroundColor: "#FE6C5F", color: "rgba(77, 85, 121, 1)" };

			return getChipColor(dayStats, chosenDepartment);
		}, [dayStats, chosenDepartment]);

		// Only show the background element when it's red
		const showBackground = useMemo(() => {
			if (!dayStats) return false;
			const percentage = Math.round((dayStats.totalHours / dayStats.wantedHours) * 100);
			return percentage > 108 || percentage < 92;
		}, [dayStats]);

		const census = useMemo<number>(() => {
			const dayObj = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo];
			const override = dayObj.census_override;
			const pccCensus = getShiftCensus(hospitalData.weeks[weekIndex], shiftNo, dayNo);

			return override ? override.override_value : pccCensus;
		}, [hospitalData.weeks, weekIndex, shiftNo, dayNo]);

		const handleAddClick = (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			trackEvent("add_nurse_clicked", {
				day_no: dayNo,
				shift_no: shiftNo,
				week_index: weekIndex,
				date: hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo]?.date?.toFormat("yyyy-MM-dd"),
				shift_type: hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type,
			});
			onAddClick(dayNo);
		};

		const handleCardClick = (e: React.MouseEvent, dayIndex: number) => {
			e.stopPropagation();
			// Check if the click target is the edit icon or its parent button
			const isEditClick = (e.target as HTMLElement).closest(".MuiIconButton-root") !== null;

			if (isEditClick) {
				return;
			}

			// If it's a non-interactive area, navigate
			const date = hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayIndex]?.date;
			trackEvent("day_schedule_navigation", {
				date: date.toFormat("yyyy-MM-dd"),
				day_no: dayIndex,
				shift_no: shiftNo,
				week_index: weekIndex,
				shift_type: hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type,
			});
			const path = `/${chosenDepartment.department_id}/day-schedule/${date.toFormat("yyyy-MM-dd")}`;
			navigate(path);
		};

		const renderNurse = (nurseInShift: NurseInShift, nurseData: Nurse, index: number) => (
			<div key={index} onClick={(e) => e.stopPropagation()} style={{ zIndex: 1 }}>
				<NurseContainer
					isAdd={false}
					dayNo={dayNo}
					weekIndex={weekIndex}
					nurseData={nurseData}
					swapWith={nurseInShift?.swapWith}
					shiftNo={shiftNo}
					message={nurseInShift?.message || ""}
					isPending={nurseInShift.isPending || false}
					isPublished={nurseInShift.isPublished || false}
					isPendingToDelete={nurseInShift.isPendingToDelete || false}
					isPendingToSwap={nurseInShift.isPendingToSwap || false}
					isPendingToMove={nurseInShift.isPendingToMove || false}
					nurseInShift={nurseInShift}
					canBeReliefCharge={nurseData.canBeReliefCharge}
					highlightedNurseId={highlightedNurseId}
				/>
			</div>
		);

		const print_nurses = (day: number, role: string, roleIndex: number) => {
			const nurseMaps: Map<number, Nurse> = new Map(hospitalData.nurses.map((nurse) => [nurse.id, nurse]));

			const allNursesForDay: NurseInShift[] = [];
			hospitalData?.weeks[weekIndex]?.weeklyShifts?.forEach((shift) => {
				const dayData = shift?.days?.[day];
				if (dayData?.nurses) {
					dayData.nurses.forEach((nurse) => {
						if (getShiftIndexByShiftTimes({ startTime: nurse.startTime, endTime: nurse.endTime }) === shiftNo) {
							allNursesForDay.push(nurse);
						}
					});
				}
			});

			return (
				<>
					{allNursesForDay.length > 0 && (
						<div className={Style.nurseContainer}>
							{allNursesForDay
								.filter((nurse: NurseInShift) => {
									if (nurse?.role !== role) return false;
									if (highlightedNurseId && nurse.nurseId === highlightedNurseId) return false;

									const nurseData = nurseMaps.get(nurse.nurseId);
									if (!nurseData) return false;

									if (!employmentTypesToDisplay.includes(nurseData.employmentType)) return false;

									if (jobManagementEnabled && selectedJobIds && selectedJobIds.length > 0) {
										if (!nurse.jobId || !selectedJobIds.includes(nurse.jobId)) return false;
									}

									return true;
								})
								.sort((a: NurseInShift, b: NurseInShift) => {
									if (a.charge && !b.charge) return -1;
									if (!a.charge && b.charge) return 1;

									const nurseA = nurseMaps.get(a.nurseId);
									const nurseB = nurseMaps.get(b.nurseId);

									// Sort by the name as displayed, so the department's name order decides the order.
									const nameA = nurseA ? formatPersonName(nurseA).toLowerCase() : "";
									const nameB = nurseB ? formatPersonName(nurseB).toLowerCase() : "";
									return nameA.localeCompare(nameB);
								})
								.map((nurseInShift: NurseInShift, index: number) => {
									const nurseData = nurseMaps.get(nurseInShift.nurseId);
									if (!nurseData) return null;
									return renderNurse(nurseInShift, nurseData, index);
								})}
						</div>
					)}
					{roleIndex < rolesToDisplay.length - 1 &&
						allNursesForDay.filter((nurse: NurseInShift) => nurseMaps.get(nurse.nurseId)?.role === rolesToDisplay[roleIndex + 1]).length > 0 && (
							<div className={Style.horizontalLine} />
						)}
				</>
			);
		};

		return (
			<div
				className={Style.container}
				key={dayNo}
				data-testid={`staffing-day-block-${hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo]?.date?.toFormat("yyyy-MM-dd")}`}
				onMouseEnter={highlightShift}
				onMouseLeave={clearShiftHighlight}
				onClick={(e) => handleCardClick(e, dayNo)}
			>
				{/* Only show background when it should be red */}
				{showBackground && (
					<div
						style={{
							position: "absolute",
							left: "2px",
							right: "2px",
							height: "56px",
							paddingTop: "4px",
							borderRadius: "16px",
							zIndex: 0,
						}}
					/>
				)}
				{(shiftNo === 0 || !isFullDay) && (
					<div className={Style.dateFont} style={{ position: "relative", zIndex: 1, top: "4px" }}>
						<div
							style={{
								background:
									hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].date.toFormat("yyyy-MM-dd") === DateTime.now().toFormat("yyyy-MM-dd")
										? "var(--inhouse-primary-alpha-08)"
										: "inherit",
								color: "var(--inhouse-primary-dark)",
								borderRadius: `${BADGE.PILL_RADIUS_FULL}px`,
								padding: "0px 10px",
							}}
						>
							{hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].date.toFormat("ccc")?.toUpperCase()}{" "}
							<span>{hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo].days[dayNo].date.toFormat("d")}</span>
						</div>
					</div>
				)}
				<div
					style={{
						marginBottom: 10,
						marginTop: 5,
						position: "relative",
						zIndex: 1,
					}}
				>
					{(shiftNo === 0 || !isFullDay) && isSnf && dayStats && <NurseHoursChip colors={chipColor} dayStats={dayStats} isActual={isActualLoaded} />}
				</div>
				<Box
					sx={{
						px: "4px",
						position: "relative",
						zIndex: 1,
						display: "flex",
						flexWrap: "nowrap",
						width: "100%",
					}}
				>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							flexWrap: "nowrap",
							width: "100%",
							background: (theme) => alpha(theme.palette.primary.main, 0.08),
							borderTopLeftRadius: "4px",
							borderTopRightRadius: "4px",
							padding: "0px 5px",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-start",
								alignItems: "center",
							}}
						>
							<CensusDisplay
								source={ChangesSource.Staffing}
								dayDate={hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].date}
								censusValue={census}
								isBlack
								tooltipContent={(() => {
									const dayObj = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo];
									const override = dayObj.census_override;
									const pccCensus = getShiftCensus(hospitalData.weeks[weekIndex], shiftNo, dayNo);
									const summaryLines = [
										...(override
											? [
													{ label: t("census.tooltip.manual"), value: override.override_value },
													{ label: censusSourceLabel, value: pccCensus },
												]
											: [{ label: t("census.tooltip.census"), value: pccCensus }]),
										...censusReplacementSummaryLines(t, getCensusSourceName(chosenDepartment, isHospitalView || isHospitalDemo), dayObj.census_replacement),
									];
									return (
										<CensusBreakdownTooltipContent
											summaryLines={summaryLines}
											mode={settings.censusByLocation}
											byDimensions={byDimensionsOf(dayObj.census_grouping)}
											totalCensus={getCensus(hospitalData.weeks[weekIndex], dayNo)}
											locations={chosenDepartment.locations}
											statsMode={chosenDepartment.statsMode}
										/>
									);
								})()}
							/>
						</Box>
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
							}}
						>
							<MuiTooltip title="# of Nurses">
								<Box sx={{ marginLeft: "10px", display: "flex", alignItems: "center" }}>
									<ThemedSvg src="/nurse.svg" alt="Nurse" size={18} color="var(--inhouse-primary)" />
									<span
										style={{
											marginLeft: "2px",
											marginRight: 0,
											fontSize: 16,
										}}
									>
										{hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.days[dayNo]?.nurses.filter((nurse) => !nurse.isPendingToDelete).length}
										{isHospital && `/ ${hospitalData?.weeks?.[weekIndex]?.weeklyShifts?.[shiftNo]?.days?.[dayNo]?.staffNo ?? 0}`}{" "}
									</span>
								</Box>
							</MuiTooltip>
							{/* Paired with the v2 grid's Post Shift icon: both open the Staffing Changes panel, and the two
							    grids are mutually exclusive branches of the same route, so one anchor name covers both.
							    Only the day the block picked — this pencil repeats once per day. */}
							<MuiTooltip title="Edit Staff">
								<IconButton data-annie-target={isPostShiftAnchor ? "staffing-post-shift" : undefined} onClick={handleAddClick} sx={{ p: 0, marginLeft: "2px" }}>
									<EditOutlined sx={{ fontSize: "16px", cursor: "pointer", color: "primary.main" }} />
								</IconButton>
							</MuiTooltip>
						</Box>
					</Box>
				</Box>
				<Box
					key={dayNo}
					className={Style.overlay}
					sx={{
						"&:hover": {
							boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.2)",
							cursor: "pointer",
						},
						boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
						overflowY: "auto",
						overflowX: "hidden",
						minHeight: "50px",
						borderRadius: 0,
						display: "flex",
						flexDirection: "column",
						flexGrow: 1,
					}}
					style={{
						// Day-cell highlight uses the secondary brand accent (tinted via color-mix
						// so it reads as a wash). Tracks the user's chosen secondary color.
						backgroundColor: isShiftIdHighlighted
							? "color-mix(in srgb, var(--inhouse-secondary) 20%, transparent)"
							: "color-mix(in srgb, var(--inhouse-secondary) 8%, transparent)",
					}}
				>
					{(() => {
						if (!highlightedNurseId) return null;
						const nurseData = hospitalData.nurses.find((n) => n.id === highlightedNurseId);
						if (!nurseData) return null;
						let highlightedShift: NurseInShift | undefined;
						hospitalData?.weeks[weekIndex]?.weeklyShifts?.forEach((shift) => {
							shift?.days?.[dayNo]?.nurses?.forEach((nurse) => {
								if (nurse.nurseId === highlightedNurseId && getShiftIndexByShiftTimes({ startTime: nurse.startTime, endTime: nurse.endTime }) === shiftNo) {
									highlightedShift = nurse;
								}
							});
						});
						if (!highlightedShift) return null;
						return (
							<>
								<div className={Style.nurseContainer}>{renderNurse(highlightedShift, nurseData, -1)}</div>
								<div className={Style.horizontalLine} />
							</>
						);
					})()}
					{rolesToDisplay.map((role: string, index: number) => (
						<React.Fragment key={role}>{print_nurses(dayNo, role, index)}</React.Fragment>
					))}
				</Box>
			</div>
		);
	},
);
